import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  MessageSquare, 
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/agencies', label: 'Agências', icon: Building2 },
  { path: '/admin/agents', label: 'Consultores', icon: Users },
  { path: '/admin/evaluations', label: 'Avaliações', icon: MessageSquare },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#F8F8F8]" data-testid="admin-layout">
      {/* Mobile header */}
      <div className="lg:hidden bg-[#212121] text-white p-4 flex items-center justify-between">
        <img 
          src="https://customer-assets.emergentagent.com/job_reviewfunnel/artifacts/wxdmy5mp_Opini%C3%A3o%20Contacto%20Directo%20%281%29.svg" 
          alt="Opinião Contacto Directo" 
          className="h-8"
        />
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-white/10"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`admin-sidebar fixed w-64 z-40 lg:translate-x-0 ${sidebarOpen ? 'open translate-x-0' : ''}`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10 hidden lg:block">
          <img 
            src="https://customer-assets.emergentagent.com/job_reviewfunnel/artifacts/wxdmy5mp_Opini%C3%A3o%20Contacto%20Directo%20%281%29.svg" 
            alt="Opinião Contacto Directo" 
            className="h-10"
          />
          <span className="text-xs text-gray-400 mt-2 block">Administração</span>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `admin-nav-item ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 bg-[#B5985A] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Terminar sessão
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
